import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Folder, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { CONCEPT_ICONS } from '../utils/concept-icons';
import {
  deskSearchResultHref,
  deskSearchResultLabel,
  deskSearchResultSubtitle,
  DESK_SEARCH_TYPE_LABEL_KEYS,
  groupDeskSearchResults,
  type DeskSearchResult,
  type DeskSearchResultType,
} from '../utils/desk-search';
import { cn } from '../lib/utils';
import { Input } from './ui/input';
import { Popover, PopoverAnchor, PopoverContent } from './ui/popover';

const SEARCH_DEBOUNCE_MS = 250;
const DESK_HOME_PATH = '/desk';

const TYPE_ICONS: Record<DeskSearchResultType, React.ComponentType<{ className?: string }>> = {
  project: Folder,
  document: CONCEPT_ICONS.document,
  note: CONCEPT_ICONS.note,
  character: CONCEPT_ICONS.character,
  location: CONCEPT_ICONS.location,
  chapter: CONCEPT_ICONS.manual_chapter,
  storyline: CONCEPT_ICONS.storyline,
  scene: CONCEPT_ICONS.scene,
  timeline_event: CONCEPT_ICONS.timeline_item,
};

type DeskSearchProps = {
  className?: string;
  inputClassName?: string;
};

export const DeskSearch: React.FC<DeskSearchProps> = ({ className, inputClassName }) => {
  const { token } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DeskSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (location.pathname !== DESK_HOME_PATH) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [location.pathname]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!token || !trimmed) {
      setResults([]);
      setLoading(false);
      setActiveIndex(-1);
      return;
    }

    setLoading(true);
    const requestId = ++requestIdRef.current;
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/desk/search?q=${encodeURIComponent(trimmed)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          throw new Error('Failed to search');
        }
        const data = (await response.json()) as { results?: DeskSearchResult[] };
        if (requestId !== requestIdRef.current) {
          return;
        }
        setResults(Array.isArray(data.results) ? data.results : []);
        setActiveIndex(-1);
        setOpen(true);
      } catch (error) {
        console.error('Desk search failed:', error);
        if (requestId === requestIdRef.current) {
          setResults([]);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [query, token]);

  const sections = groupDeskSearchResults(results);
  const flatResults = sections.flatMap((section) => section.results);
  const showDropdown = open && query.trim().length > 0;

  const selectResult = (result: DeskSearchResult): void => {
    const href = deskSearchResultHref(result);
    if (!href) {
      return;
    }
    setQuery('');
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
    navigate(href);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!showDropdown) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => {
        if (flatResults.length === 0) {
          return -1;
        }
        return current < flatResults.length - 1 ? current + 1 : 0;
      });
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => {
        if (flatResults.length === 0) {
          return -1;
        }
        return current <= 0 ? flatResults.length - 1 : current - 1;
      });
      return;
    }
    if (event.key === 'Enter' && activeIndex >= 0 && flatResults[activeIndex]) {
      event.preventDefault();
      selectResult(flatResults[activeIndex]);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  let optionOffset = 0;

  return (
    <Popover
      modal={false}
      open={showDropdown}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setOpen(false);
          setActiveIndex(-1);
        }
      }}
    >
      <div data-testid="desk-search" className={cn('relative w-full max-w-md', className)}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setOpen(true);
              }}
              onFocus={() => {
                if (query.trim()) {
                  setOpen(true);
                }
              }}
              onKeyDown={onKeyDown}
              placeholder={t('desk_search_placeholder')}
              aria-label={t('desk_search_placeholder')}
              aria-autocomplete="list"
              aria-controls={showDropdown ? listboxId : undefined}
              aria-expanded={showDropdown}
              role="combobox"
              className={cn('pl-9', inputClassName)}
              autoComplete="off"
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          id={listboxId}
          role="listbox"
          data-testid="desk-search-results"
          align="center"
          sideOffset={6}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          className="z-[60] w-[var(--radix-popover-trigger-width)] max-h-80 overflow-y-auto p-1"
        >
          {loading && results.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              {t('loading')}
            </div>
          ) : flatResults.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              {t('desk_search_no_results')}
            </div>
          ) : (
            sections.map((section) => {
              const sectionStart = optionOffset;
              optionOffset += section.results.length;
              const Icon = TYPE_ICONS[section.type];
              return (
                <div key={section.type} className="flex flex-col gap-0.5 py-1">
                  <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                    {t(DESK_SEARCH_TYPE_LABEL_KEYS[section.type])}
                  </div>
                  {section.results.map((result, index) => {
                    const flatIndex = sectionStart + index;
                    const subtitle = deskSearchResultSubtitle(result);
                    const active = flatIndex === activeIndex;
                    return (
                      <button
                        key={`${result.type}-${result.id}`}
                        type="button"
                        role="option"
                        aria-selected={active}
                        className={cn(
                          'flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent',
                          active && 'bg-accent'
                        )}
                        onMouseEnter={() => setActiveIndex(flatIndex)}
                        onClick={() => selectResult(result)}
                      >
                        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium text-foreground">
                            {deskSearchResultLabel(result) || t('untitled')}
                          </span>
                          {subtitle ? (
                            <span className="block truncate text-xs text-muted-foreground">
                              {subtitle}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </PopoverContent>
      </div>
    </Popover>
  );
};
